import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { EnterpriseSidebar } from './EnterpriseSidebar';
import { EnterpriseHeader } from './EnterpriseHeader';
import { EnterpriseCommandPalette } from '../enterprise/EnterpriseCommandPalette';
import { EnterpriseBreadcrumbs } from '../enterprise/EnterpriseBreadcrumbs';
import { ADMIN_BASE } from '../../config/adminLayers';

function getAdminPageTitle(pathname: string): string {
  const base = ADMIN_BASE;
  if (pathname === base || pathname === `${base}/` || pathname === `${base}/dashboard`) {
    return 'Dashboard';
  }
  if (!pathname.startsWith(`${base}/`)) {
    return 'Admin';
  }
  const rest = pathname.slice(base.length + 1);

  if (rest === 'workspaces') return 'Workspaces';
  if (rest.startsWith('workspaces/')) return 'Workspace overview';
  if (rest === 'verified-domains') return 'Sending domains';
  if (rest === 'audit') return 'Audit log';
  if (rest === 'settings') return 'Settings';
  if (rest === 'settings/invoice-templates') return 'Invoice templates';

  const parts = rest.split('/').filter(Boolean);
  if (parts.length >= 2 && parts[0] === 'users') return 'User detail';

  const SEGMENT: Record<string, string> = {
    dashboard: 'Dashboard',
    users: 'Users',
    pricing: 'Pricing',
    invoices: 'Invoices',
    'tds-followup': 'TDS / 26AS',
    subscriptions: 'Subscriptions',
    'phone-numbers': 'Phone numbers',
    'calling-providers': 'Calling providers',
    'api-keys': 'API keys',
    workflows: 'Workflows',
    'workflow-health': 'Workflow health',
    integrations: 'Integrations',
    settings: 'Settings',
    system: 'System',
    monitoring: 'Monitoring',
    'fastapi-monitoring': 'FastAPI monitoring',
    'copilot-metrics': 'Copilot metrics',
    'latency-profiler': 'Latency Profiler',
    'supabase-monitor': 'Supabase monitor',
    operations: 'Operations Center',
    'voice-infrastructure': 'Voice Infrastructure',
    'platform-operations': 'Platform Operations',
    'jarvis-tools': 'Jarvis tools',
  };
  return SEGMENT[parts[0]] ?? 'Admin';
}

export const EnterpriseAdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const location = useLocation();
  const title = getAdminPageTitle(location.pathname);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="min-h-screen bg-enterprise-50">
      <EnterpriseCommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} />
      <EnterpriseSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="lg:pl-72">
        <EnterpriseHeader
          setSidebarOpen={setSidebarOpen}
          title={title}
          onOpenCommandPalette={() => setCommandOpen(true)}
        />
        <main className="px-4 py-6 sm:px-6 lg:px-8">
          <EnterpriseBreadcrumbs />
          <Outlet />
        </main>
      </div>
    </div>
  );
};
